<template>
  <span
    :class="
      twMerge(
        'inline-flex items-center rounded-full border text-center font-medium select-none',
        colors[color],
        isInteractive && interactiveColors[color],
        sizes[size],
      )
    "
    v-tooltip="tooltip"
  >
    <slot />

    <button
      v-if="removable"
      class="-m-1 ml-1 rounded-full p-1 transition-colors"
      :class="removeButtonColors[color]"
      @click="emit('remove')"
    >
      <CloseIcon class="size-3.5" />
    </button>
  </span>
</template>

<script setup lang="ts">
import CloseIcon from '~icons/lucide/x'
import { twMerge } from 'tailwind-merge'

defineOptions({
  name: 'BaseTag',
})

const emit = defineEmits(['remove'])

export type TagSize = 'small' | 'regular' | 'large'
export type TagColor =
  | 'gray'
  | 'secondary'
  | 'outline-secondary'
  | 'green'
  | 'red'
  | 'yellow'
  | 'purple'

type Props = {
  size?: TagSize
  color?: TagColor
  tooltip?: string
  isInteractive?: boolean
  removable?: boolean
}

withDefaults(defineProps<Props>(), {
  size: 'regular',
  color: 'gray',
  tooltip: '',
  isInteractive: false,
  removable: false,
})

const colors = {
  gray: 'text-neutral-500 bg-neutral-50 border-neutral-50',
  secondary: 'text-primary-500 bg-primary-50 border-primary-50',
  'outline-secondary': 'text-primary-500 bg-white border-primary-100',
  green: 'text-success-700 bg-success-50 border-success-50',
  red: 'text-error-700 bg-error-50 border-error-50',
  yellow: 'text-warning-900 bg-warning-50 border-warning-50',
  purple: 'text-secondary-700 bg-secondary-50 border-secondary-50',
}

const interactiveColors: Partial<Record<TagColor, string>> = {
  'outline-secondary':
    'transition-colors hover:bg-primary-50 hover:text-primary-500 hover:border-primary-50',
}

const sizes = {
  small: 'text-[11px] py-1 px-2',
  regular: 'text-xs py-[6px] px-3',
  large: 'text-sm py-2 px-3',
}

const removeButtonColors: Partial<Record<TagColor, string>> = {
  purple: 'hover:bg-secondary-100',
}
</script>
